import React, { useEffect, useState, useRef } from "react";
import "./FilterPage.css";
import { useNavigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { AppHeader } from "./AppHeader";
import ProBrokerFooter from "./ProBrokerFooter";
import { useUserProfile, DEFAULT_PROPERTY_CITIES } from "../contexts/UserProfileContext";
import { FEATURES } from "../constants/feature.constants";
import { fetchAreas } from "../common/areasApi";
import apiClient from "../common/apiClient";
import { toast } from "react-toastify";

const FilterPage = () => {
  const location = useLocation();
  const initialType = location.state?.type || "Residential Rent";
  const { userProfile, hasFeature, hasCityAccess, propertyAccessCities } = useUserProfile();

  // Driven by PropertyAccessCity: only the cities the user actually has access to
  // are rendered. Falls back to the default list until the profile resolves.
  const cityOptions = propertyAccessCities.length ? propertyAccessCities : DEFAULT_PROPERTY_CITIES;

  const normalizeText = (value) => String(value || "").trim().toLowerCase();

  const isPlanActive = () => {
    // Check if user has any active features
    if (!userProfile) return false;
    
    const checkFeaturesArray = (features) => {
      if (!Array.isArray(features)) return false;
      return features.some(feature => {
        const isActive = (feature?.status || "").toString().toLowerCase() === "active";
        return isActive;
      });
    };
    
    if (checkFeaturesArray(userProfile.FeaturesAccessData)) return true;
    if (checkFeaturesArray(userProfile.user?.FeaturesAccessData)) return true;
    if (checkFeaturesArray(userProfile.paymentHistory?.[0]?.FeaturesAccessData)) return true;
    
    return false;
  };

  const shouldLockTabs = !isPlanActive();

  const isTabDisabled = (tab) => {
    if (shouldLockTabs) return true;
    if (tab === "owner") return !hasFeature(FEATURES.OWNER_PROPERTY);
    if (tab === "broker" || tab === "requirement") return !hasFeature(FEATURES.BROKER_PROPERTY);
    if (tab === "builder") return !hasFeature(FEATURES.BUILDER_PROPERTY);
    return false;
  };

  const status = "active";
  const [searchQuery, setSearchQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchInputRef = useRef(null);
  const [areas, setAreas] = useState([]); // Loaded per selected city + section
  const [isLoadingAreas, setIsLoadingAreas] = useState(false);
  const [visibleAreasCount, setVisibleAreasCount] = useState(
    window.innerWidth < 768 ? 14 : 35
  ); // Number of areas shown while the list is collapsed
  const [isShowingAllAreas, setIsShowingAllAreas] = useState(false); // New state to track if showing all areas

  const [filters, setFilters] = useState({
    type: initialType, // Default selected type
    city: "Ahmedabad West", // ✅ Default selected city
    furnishedTypes: [],
    areas: [], // plural
    area: [], // singular ✅ added
    bhks: [],
    bhks: [],
    furnishingPreferences: [],
    occupencyType: [],
    subType: [],
    floors: [], // ✅ new filter
    minRent: "",
    maxRent: "",
    status: "active",
    listedDate: "",
    minsqFt: "",
    maxsqFt: "",
    minsqYd: "",
    maxsqYd: "",
    sizeUnit: "sqFt", // "sqFt" | "sqYd"
    verifiedOnly: false,
    livingTypeFilter: "",
    age: "",
    propertyAge: [],
    bathrooms: "",
  });

  const [activeTab, setActiveTab] = useState("owner");
  const navigate = useNavigate();

  useEffect(() => {
    if (isTabDisabled(activeTab)) {
      const availableTabs = ["owner", "broker", "builder", "requirement"].filter(tab => !isTabDisabled(tab));
      if (availableTabs.length > 0) {
        setActiveTab(availableTabs[0]);
      }
    }
  }, [userProfile, activeTab]);

  // If the selected city isn't in the user's PropertyAccessCity list, auto-switch
  // to the first accessible city (e.g. default "Ahmedabad West" -> "Ahmedabad East").
  useEffect(() => {
    if (propertyAccessCities.length === 0) return; // no restriction / not loaded yet
    if (hasCityAccess(filters.city)) return; // current selection already allowed
    const firstAllowed = cityOptions.find((c) => hasCityAccess(c));
    if (firstAllowed && firstAllowed !== filters.city) {
      setFilters((prev) => ({ ...prev, city: firstAllowed }));
      localStorage.setItem("selectedCity", firstAllowed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyAccessCities.join("|"), filters.city]);

  const clearFilters = () => {
    // Clear the filters state
    setFilters({
      type: initialType,
      city: "Ahmedabad West",
      furnishedTypes: [],
      areas: [],
      area: [],
      bhks: [],
      bhks: [],
      furnishingPreferences: [],
      occupencyType: [],
      subType: [],
      floors: [], // ✅ new filter
      minRent: "",
      maxRent: "",
      status: "active",
      listedDate: "",
      minsqFt: "",
      maxsqFt: "",
      minsqYd: "",
      maxsqYd: "",
      sizeUnit: "sqFt",
      verifiedOnly: false,
      livingTypeFilter: "", // ✅ Reset familyFilter
      age: "",
      propertyAge: [],
      bathrooms: "",
    });

    // Remove filters from localStorage
    localStorage.removeItem("filters");
  };

  // Retrieve filters from localStorage on component mount
  useEffect(() => {
    const storedFilters = localStorage.getItem("filters");

    if (storedFilters) {
      const parsedFilters = JSON.parse(storedFilters);

      // Set the active tab if it was stored
      if (parsedFilters.activeTab) {
        setActiveTab(parsedFilters.activeTab);
        delete parsedFilters.activeTab; // Remove it from filters
      }

      // Normalize arrays to avoid runtime errors when reading .length/.includes
      const asArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);

      setFilters({
        ...parsedFilters,
        city: parsedFilters.city || "Ahmedabad West",
        furnishedTypes: asArray(parsedFilters.furnishedTypes),
        furnishingPreferences: asArray(parsedFilters.furnishingPreferences),
        areas: asArray(parsedFilters.areas),
        area: asArray(parsedFilters.area),
        bhks: asArray(parsedFilters.bhks),
        bhks: asArray(parsedFilters.bhks),
        occupencyType: asArray(parsedFilters.occupencyType),
        subType: asArray(parsedFilters.subType),
        propertyAge: asArray(parsedFilters.propertyAge),
        floors: asArray(parsedFilters.floors),
        sizeUnit: parsedFilters.sizeUnit || "sqFt",
        minsqYd: Number(parsedFilters.minsqYd) || 0,
        maxsqYd: Number(parsedFilters.maxsqYd) || 0,
        livingTypeFilter: parsedFilters.livingTypeFilter || "",
      });
    }
  }, []);

  const handleCheckboxChange = (event) => {
    const { name, value, checked } = event.target;

    // Directly update the filters state to ensure UI updates
    setFilters((prevState) => {
      const safeArray = Array.isArray(prevState[name])
        ? prevState[name]
        : prevState[name]
          ? [prevState[name]]
          : [];
      const updatedAreas = checked
        ? [...safeArray, value] // Add value if checked
        : safeArray.filter((item) => item !== value); // Remove value if unchecked

      return {
        ...prevState,
        [name]: updatedAreas,
      };
    });
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFilters((prevState) => {
      // Check if the type has changed
      if (name === "type") {
        let resetSubType = [];
        let resetBhks = [];

        // Reset subType if switching between residential and commercial
        if (
          (value === "Residential Rent" || value === "Residential Sell") &&
          (prevState.type === "Commercial Rent" ||
            prevState.type === "Commercial Sell")
        ) {
          resetSubType = []; // Reset subType for Residential
          resetBhks = []; // Reset bhk for Residential
        } else if (
          (value === "Commercial Rent" || value === "Commercial Sell") &&
          (prevState.type === "Residential Rent" ||
            prevState.type === "Residential Sell")
        ) {
          resetSubType = []; // Reset subType for Commercial
          resetBhks = []; // Reset bhk for Commercial
        }

        return {
          ...prevState,
          [name]: value,
          subType: resetSubType, // Clear subType when type changes
          bhks: resetBhks, // Clear bhks when type changes
          livingTypeFilter:
            value !== "Residential Rent" ? "" : prevState.livingTypeFilter, // Reset familyFilter if not Residential Rent
        };
      }

      // Each city has its own area list, so selections from the previous city
      // would no longer be offered (or match anything) once the city changes.
      if (name === "city" && value !== prevState.city) {
        return {
          ...prevState,
          city: value,
          areas: [],
          area: [],
        };
      }

      // For all other input changes
      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  // Module type of the selected section
  const getModuleType = () => {
    if (activeTab === "broker") return "broker";
    if (activeTab === "builder") return "builder";
    if (activeTab === "requirement") return "requirement";
    return "owner";
  };

  const normalizePrice = (price) => {
    if (!price || price === "") return 0;

    let s = price.toString().toLowerCase().trim();

    // Remove all spaces
    s = s.replace(/\s+/g, "");

    // Check for text units first
    const hasLac = s.includes("lac") || s.includes("lakh");
    const hasCr = s.includes("cr") || s.includes("crore");
    const hasK = s.includes("k") && !hasLac && !hasCr; // Only if not part of lac/cr

    // Extract numeric part (allow decimals and commas)
    const numberPart = parseFloat(s.replace(/[^0-9.]/g, "")) || 0;

    if (hasLac) {
      return numberPart * 100000;
    } else if (hasCr) {
      return numberPart * 10000000;
    } else if (hasK) {
      return numberPart * 1000;
    } else {
      // No units - treat as direct number (just remove commas)
      return Number(s.replace(/,/g, ""));
    }
  };

  // Helper to format raw numbers into Lac/Cr
  const formatInputPrice = (value) => {
    if (!value) return "";

    let cleanValue = value.toString().replace(/,/g, "").trim();

    // Check if it already has units
    if (/[a-zA-Z]/.test(cleanValue)) {
      return value;
    }

    const num = parseFloat(cleanValue);
    if (isNaN(num)) return value;

    if (num >= 10000000) {
      return (num / 10000000).toFixed(2).replace(/\.0+$/, "") + " Cr";
    } else if (num >= 100000) {
      return (num / 100000).toFixed(2).replace(/\.0+$/, "") + " Lac";
    }

    return value;
  };

  // 🔖 Saved Search — lets a broker store the current filter combo and get
  // notified of new matches later, instead of re-running the same search by
  // hand. Field names mirror models/SavedSearch.js on the backend.
  const [saveSearchName, setSaveSearchName] = useState("");
  const [isSavingSearch, setIsSavingSearch] = useState(false);

  const handleSaveSearch = async () => {
    const token = Cookies.get("token");
    if (!token) {
      toast.error("Please log in to save a search.");
      return;
    }

    const selectedAreasForSave =
      filters.areas && filters.areas.length > 0
        ? filters.areas
        : filters.area && filters.area.length > 0
          ? (Array.isArray(filters.area) ? filters.area : [filters.area])
          : [];

    setIsSavingSearch(true);
    const result = await apiClient(
      "post",
      "/cjidnvij/ceksfbuebijn/user/saved-searches",
      {
        name: saveSearchName || `${filters.type || "Any"} · ${selectedAreasForSave.join(", ") || filters.city}`,
        type: filters.type === "All" ? "" : filters.type,
        areas: selectedAreasForSave,
        bhk: filters.bhks || [],
        furnishingPreferences: filters.furnishingPreferences || [],
        priceRange: { min: Number(filters.minRent) || 0, max: Number(filters.maxRent) || 0 },
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setIsSavingSearch(false);

    if (result.success) {
      toast.success("Search saved — you'll see it under Saved Searches.");
      setSaveSearchName("");
    } else {
      toast.error(result.error?.message || "Could not save this search. Please try again.");
    }
  };

  // Update the handleApplyFilters function
  const handleApplyFilters = () => {
    let payload = {
      ...filters,
      type: filters.type === "All" ? "" : filters.type,
    };

    // ✅ Ensure areas are properly formatted as array
    // Ensure correct array of areas
    const selectedAreas =
      filters.areas && filters.areas.length > 0
        ? filters.areas
        : filters.area && filters.area.length > 0
          ? Array.isArray(filters.area)
            ? filters.area
            : [filters.area]
          : [];

    // console.log(filters);

    // Add areas to payload
    if (selectedAreas.length > 0) {
      payload.areas = selectedAreas;
    }

    let targetUrl = "/residential-rental";

    if (activeTab === "broker" || activeTab === "builder") {
      targetUrl = `/residential-rental/${activeTab}`;
      payload = {
        brokerId: Cookies.get("userId") || "",
        listedBy: activeTab,
        type: filters.type === "All" ? "" : filters.type,
        subType: filters.subType || [],
        unitType:
          filters.subType && filters.subType.length > 0
            ? Array.isArray(filters.subType)
              ? filters.subType
              : [filters.subType]
            : [],

        occupencyType:
          filters.livingTypeFilter && filters.livingTypeFilter.length > 0
            ? filters.livingTypeFilter.split("|")
            : [],

        bhks: filters.bhks || [],
        furnishedTypes: filters.furnishedTypes || [],
        propertyCurrentStatus: "Available",
        verifyStatus: filters.verifyStatus || "verified",
        city: filters.city || "",
        minsqFt: filters.minsqFt || 0,
        maxsqFt: filters.maxsqFt || 0,

        // ✅ Fixed: Properly send areas as array
        areas: selectedAreas,

        page: 0,
        size: 25,
        priceMin: filters.minRent
          ? Number(normalizePrice(filters.minRent))
          : "",
        priceMax: filters.maxRent
          ? Number(normalizePrice(filters.maxRent))
          : "",
        propertyAge: filters.propertyAge || [],
        age: filters.age || "",
        bathrooms: filters.bathrooms || "",
        userId: Cookies.get("userId") || "",
      };
    } else if (activeTab === "requirement") {
      targetUrl = `/residential-rental/requirement`;
      payload = {
        type: filters.type === "All" ? "" : filters.type,
        listedBy: "requirement",
        city: filters.city || "",
        subType: filters.subType || [],
        areas: selectedAreas, // ✅ Fixed: Use selectedAreas
        priceRange: {
          min: Number(filters.minRent) || 0,
          max: Number(filters.maxRent) || 100000, // Provide a sensible default max
        },
        propertyAge: filters.propertyAge || [],
        bhks: filters.bhks || [],
        furnishingPreferences:
          filters.furnishingPreferences &&
            filters.furnishingPreferences.length > 0
            ? filters.furnishingPreferences
            : filters.furnishedTypes || [],
        requirementStatus: "active",
        sizeRange:
          filters.sizeUnit === "sqYd"
            ? {
              sqFt: { min: 0, max: 0 },
              sqYd: {
                min: Number(filters.minsqYd) || 0,
                max: Number(filters.maxsqYd) || 0,
              },
            }
            : {
              sqFt: {
                min: Number(filters.minsqFt) || 0,
                max: Number(filters.maxsqFt) || 0,
              },
              sqYd: { min: 0, max: 0 },
            },
      };
    } else {
      // ✅ Owner properties - ensure areas are included
      payload.areas = selectedAreas;
      payload.propertyAge = filters.propertyAge || [];
    }

    localStorage.setItem(
      "filters",
      JSON.stringify({
        ...payload,
        activeTab,
      })
    );

    navigate(targetUrl, {
      state: {
        filters: payload,
        type: filters.type,
        routeType: activeTab,
      },
    });
  };

  // Areas are stored per city and per moduleType, so the list is loaded for
  // whichever city and section are selected.
  useEffect(() => {
    if (!filters.city) return;

    let cancelled = false;

    const loadAreas = async () => {
      setIsLoadingAreas(true);
      const names = await fetchAreas(filters.city, getModuleType());

      if (cancelled) return; // A newer city/section was picked while in flight

      setAreas(names);
      setIsLoadingAreas(false);
    };

    loadAreas();

    return () => {
      cancelled = true;
    };
    // getModuleType() is derived from activeTab
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.city, activeTab]);

  const propertyAgeOptions = [
    "1-5 Years",
    "5-10 Years",
    "10-15 Years",
    "15-20 Years",
    "20-25 Years",
    "25-30 Years",
    "30-35 Years",
    "35-40 Years",
    "40-45 Years",
    "45-50 Years",
    "50 & Above",
    "Newly Constructed",
  ];

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    // Only show suggestions if there is a query
    setShowSuggestions(e.target.value.length > 0);
    setHighlightedIndex(-1); // Reset highlight when typing
  };

  const handleSuggestionClick = (area) => {
    // Add the selected area to filters if not already included
    if (!(Array.isArray(filters.areas) ? filters.areas : []).includes(area)) {
      setFilters((prev) => ({
        ...prev,
        areas: [...(Array.isArray(prev.areas) ? prev.areas : []), area],
      }));
    }
    setSearchQuery(""); // Clear the search input after selection
    setShowSuggestions(false);
    setHighlightedIndex(-1); // Reset highlight after selection
    searchInputRef.current.focus(); // Focus the input after selection
  };

  // Add a function to handle clicking outside the suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      const searchContainer = document.querySelector(".area-search-container");
      // Check if the clicked element is NOT within the suggestions or the search container itself
      if (
        searchContainer &&
        !searchContainer.contains(event.target) &&
        !event.target.closest(".area-suggestions")
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Function to remove an area chip
  const handleRemoveArea = (areaToRemove) => {
    setFilters((prev) => ({
      ...prev,
      areas: prev.areas.filter((area) => area !== areaToRemove),
    }));
  };

  // Ensure both "areas" and "area" are always arrays
  const safeAreas = Array.isArray(filters.areas)
    ? filters.areas
    : filters.areas
      ? [filters.areas]
      : [];
  const safeArea = Array.isArray(filters.area)
    ? filters.area
    : filters.area
      ? [filters.area]
      : [];

  const filteredAreas = areas.filter(
    (area) =>
      area.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !safeAreas.includes(area) &&
      !safeArea.includes(area)
  );

  const displayedAreas = isShowingAllAreas
    ? areas
    : areas.slice(0, visibleAreasCount);

  // Add window resize handler
  useEffect(() => {
    const handleResize = () => {
      setVisibleAreasCount(window.innerWidth < 768 ? 14 : 35);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleShowMoreAreas = () => {
    setIsShowingAllAreas(true); // Update state to show we're displaying all areas
  };

  // New function to handle showing less areas
  const handleShowLessAreas = () => {
    setIsShowingAllAreas(false); // Update state to show we're not displaying all areas
  };

  // Add keyboard navigation for suggestions
  const handleKeyDown = (e) => {
    if (!showSuggestions || filteredAreas.length === 0) {
      if (e.key === "Enter" && searchQuery) {
        const matchingArea = areas.find(
          (area) => area.toLowerCase() === searchQuery.toLowerCase()
        );
        if (matchingArea && !filters.areas.includes(matchingArea)) {
          setFilters((prev) => ({
            ...prev,
            areas: [...prev.areas, matchingArea],
          }));
          setSearchQuery("");
        }
        setShowSuggestions(false);
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prevIndex) =>
        prevIndex === filteredAreas.length - 1 ? 0 : prevIndex + 1
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prevIndex) =>
        prevIndex === 0 || prevIndex === -1
          ? filteredAreas.length - 1
          : prevIndex - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex !== -1) {
        handleSuggestionClick(filteredAreas[highlightedIndex]);
      }
    } else if (e.key === "Backspace" && searchQuery === "") {
      if (filters.areas.length > 0) {
        const lastArea = filters.areas[filters.areas.length - 1];
        handleRemoveArea(lastArea);
      }
    }
  };

  const handleTabChange = (tab) => {
    if (isTabDisabled(tab)) return;
    setActiveTab(tab);

    // Set default type based on tab
    let defaultType = initialType;
    if (tab === "builder") {
      defaultType = "Residential"; // Auto-select Residential for builder tab
    }

    // Reset filters when changing tabs
    setFilters({
      ...filters,
      type: defaultType,
      furnishedTypes: [],
      areas: [],
      bhks: [],
      subType: [],
      minRent: "",
      maxRent: "",
      status: "active",
      listedDate: "",
      minsqFt: "",
      maxsqFt: "",
      minsqYd: "",
      maxsqYd: "",
      sizeUnit: "sqFt",
      verifiedOnly: false,
      livingTypeFilter: "",
    });
  };

  return (
    <>
      <AppHeader onMenuToggle={() => { }} />
      <div className="filter-page relative" style={{ background: "#FAF7FF" }}>
        <div className="flex items-center mt-3 pb-2 justify-between md:hidden">

            {/* LEFT SIDE - BACK */}
            <button
              className="flex items-center gap-1"
              onClick={() => navigate(-1)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M19 12H5" />
                <path d="M12 19l-7-7 7-7" />
              </svg>
              <span>Back</span>
            </button>

            {/* RIGHT SIDE - CLEAR FILTER */}
            <button
              onClick={clearFilters}
              className="bg-red-600 text-white px-3 py-1 rounded-full text-sm"
            >
              Clear Filter
            </button>
          </div>
        
      {/* CITY SECTION */}
      <div className="mobile-hide">
        <h3 className="lg:hidden">SELECT CITY</h3>
      </div>
      <div className="filter-section1 md:ml-[-20px]">
        <div className="filter-options1">
          <div className="pt-4 right-[90px] text-[#503691] relative hidden lg:block text-style">
            SELECT CITY
          </div>

          {cityOptions.map(
            (cityOption) => {
              const cityAllowed = hasCityAccess(cityOption);
              return (
                <label
                  key={cityOption}
                  className={`radio-button ${filters.city === cityOption ? "selected" : ""} ${!cityAllowed ? "opacity-50 cursor-not-allowed" : ""}`}
                  title={!cityAllowed ? "No access to this city" : undefined}
                >
                  <input
                    type="radio"
                    name="city"
                    value={cityOption}
                    onChange={handleInputChange}
                    checked={filters.city === cityOption}
                    disabled={!cityAllowed}
                  />
                  {cityOption}
                </label>
              );
            }
          )}
        </div>
      </div>

      <hr className="relative w-full mt-4 left-0 md:w-[98%] md:left-[120px]" />

      <div className="mobile-hide">
        <h3 className="lg:hidden">SELECT SECTION</h3>
      </div>
      <div className="filter-section1 md:ml-[-20px]">
        <div className="filter-options1">
          <div className="pt-4 right-[90px] text-[#503691] relative hidden lg:block text-style">
            SELECT SECTION
          </div>

          {/* Tab buttons using the same structure as PROPERTY TYPE radio buttons */}
          <label
            className={`radio-button ${activeTab === "owner" ? "selected" : ""} ${isTabDisabled("owner") ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <input
              type="radio"
              name="section"
              value="owner"
              onChange={() => handleTabChange("owner")}
              checked={activeTab === "owner"}
              disabled={isTabDisabled("owner")}
            />
            Owner Properties
          </label>
          <label
            className={`radio-button ${activeTab === "broker" ? "selected" : ""} ${isTabDisabled("broker") ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <input
              type="radio"
              name="section"
              value="broker"
              onChange={() => handleTabChange("broker")}
              checked={activeTab === "broker"}
              disabled={isTabDisabled("broker")}
            />
            Broker Properties
          </label>
          <label
            className={`radio-button ${activeTab === "requirement" ? "selected" : ""} ${isTabDisabled("requirement") ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <input
              type="radio"
              name="section"
              value="requirement"
              onChange={() => handleTabChange("requirement")}
              checked={activeTab === "requirement"}
              disabled={isTabDisabled("requirement")}
            />
            Requirements
          </label>
          {/* <label
            className={`radio-button ${activeTab === "builder" ? "selected" : ""} ${isTabDisabled("builder") ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <input
              type="radio"
              name="section"
              value="builder"
              onChange={() => handleTabChange("builder")}
              checked={activeTab === "builder"}
              disabled={isTabDisabled("builder")}
            />
            Builder Properties
          </label> */}
          <div className="clear-filters-desktop ">
                <button onClick={clearFilters} className="clear-filters-button">
                  Clear Filter
                </button>
              </div>  
        </div>
        
      </div>


      <hr className="relative w-full mt-4 left-0 md:w-[98%] md:left-[120px]" />

      <div className="filter-grid">
        <div className="">
          <div className="mobile-hide flex flex-row ">
            <h3 className="lg:hidden">PROPERTY TYPE</h3>

            
          </div>
          <div className="filter-section1  md:ml-[-20px]">
            <div className="filter-options1">
              <div className="pt-4 right-[90px] text-[#503691] relative hidden lg:block text-style">
                PROPERTY TYPE
              </div>

              {activeTab === "builder" ? (
                // Builder-specific property types
                <>
                  <label
                    className={`radio-button ${filters.type === "Residential" ? "selected" : ""
                      }`}
                  >
                    <input
                      type="radio"
                      name="type"
                      value="Residential"
                      onChange={handleInputChange}
                      checked={filters.type === "Residential"}
                    />
                    Residential
                  </label>
                  <label
                    className={`radio-button ${filters.type === "Commercial" ? "selected" : ""
                      }`}
                  >
                    <input
                      type="radio"
                      name="type"
                      value="Commercial"
                      onChange={handleInputChange}
                      checked={filters.type === "Commercial"}
                    />
                    Commercial
                  </label>
                </>
              ) : (
                // Original property types for other tabs
                <>
                  <label
                    className={`radio-button ${filters.type === "Residential Rent" ? "selected" : ""
                      }`}
                  >
                    <input
                      type="radio"
                      name="type"
                      value="Residential Rent"
                      onChange={handleInputChange}
                      checked={filters.type === "Residential Rent"}
                    />
                    Resident Rent
                  </label>
                  <label
                    className={`radio-button ${filters.type === "Residential Sell" ? "selected" : ""
                      }`}
                  >
                    <input
                      type="radio"
                      name="type"
                      value="Residential Sell"
                      onChange={handleInputChange}
                      checked={filters.type === "Residential Sell"}
                    />
                    Resident Sell
                  </label>
                  <label
                    className={`radio-button ${filters.type === "Commercial Rent" ? "selected" : ""
                      }`}
                  >
                    <input
                      type="radio"
                      name="type"
                      value="Commercial Rent"
                      onChange={handleInputChange}
                      checked={filters.type === "Commercial Rent"}
                    />
                    Commercial Rent
                  </label>
                  <label
                    className={`radio-button ${filters.type === "Commercial Sell" ? "selected" : ""
                      }`}
                  >
                    <input
                      type="radio"
                      name="type"
                      value="Commercial Sell"
                      onChange={handleInputChange}
                      checked={filters.type === "Commercial Sell"}
                    />
                    Commercial Sell
                  </label>
                </>
              )}

              
            </div>
          </div>
          <hr className="relative w-full left-0 md:w-[98%] md:left-[120px]" />

          <div className="mobile-hide">
            <h3 className="lg:hidden">FURNISHED</h3>
          </div>
          <div className="filter-section1 lg:ml-[-20px]">
            <div className="filter-options2">
              <div className="pt-4 right-[90px] text-[#503691] relative hidden lg:block text-style">
                FURNISHED TYPE
              </div>
              <div className="filter-options2">
                <label>
                  <input
                    className="checkbox"
                    type="checkbox"
                    name={
                      activeTab === "requirement"
                        ? "furnishingPreferences"
                        : "furnishedTypes"
                    }
                    value="Furnished"
                    checked={
                      activeTab === "requirement"
                        ? filters.furnishingPreferences.includes("Furnished")
                        : filters.furnishedTypes.includes("Furnished")
                    }
                    onChange={handleCheckboxChange}
                  />{" "}
                  Furnished
                </label>

                <label>
                  <input
                    className="checkbox"
                    type="checkbox"
                    name={
                      activeTab === "requirement"
                        ? "furnishingPreferences"
                        : "furnishedTypes"
                    }
                    value="Semi-Furnished"
                    checked={
                      activeTab === "requirement"
                        ? filters.furnishingPreferences.includes(
                          "Semi-Furnished"
                        )
                        : filters.furnishedTypes.includes("Semi-Furnished")
                    }
                    onChange={handleCheckboxChange}
                  />{" "}
                  Semi-Furnished
                </label>
                <label>
                  <input
                    className="checkbox"
                    type="checkbox"
                    name={
                      activeTab === "requirement"
                        ? "furnishingPreferences"
                        : "furnishedTypes"
                    }
                    value="Kitchen-Fix"
                    checked={
                      activeTab === "requirement"
                        ? filters.furnishingPreferences.includes("Kitchen-Fix")
                        : filters.furnishedTypes.includes("Kitchen-Fix")
                    }
                    onChange={handleCheckboxChange}
                  />{" "}
                  Kitchen - Fix
                </label>
                <label>
                  <input
                    className="checkbox"
                    type="checkbox"
                    name={
                      activeTab === "requirement"
                        ? "furnishingPreferences"
                        : "furnishedTypes"
                    }
                    value="Unfurnished"
                    checked={
                      activeTab === "requirement"
                        ? filters.furnishingPreferences.includes("Unfurnished")
                        : filters.furnishedTypes.includes("Unfurnished")
                    }
                    onChange={handleCheckboxChange}
                  />{" "}
                  Unfurnished
                </label>
              </div>
            </div>
          </div>
          <hr className="relative w-full left-0 md:w-[98%] md:left-[120px]" />
          <div className="mobile-hide">
            <h3 className="lg:hidden">AREA</h3>
          </div>
          <div className="filter-section2 lg:mt-[-10px] lg:ml-28  ">
            <div className="bottom-[-220px] right-[190px] text-[#503691] relative hidden lg:block sqarea-font"></div>
            <div className="filter-options">
              {/* Add search box for areas */}
              <div className="area-search-container gap-3  ">
                <div className="w-full lg:w-1/2 md:w-full flex items-center justify-end">
                  <input
                    type="text"
                    ref={searchInputRef}
                    placeholder={
                      filters.areas.length > 0
                        ? "Add more areas..."
                        : "Search areas..."
                    }
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onKeyDown={handleKeyDown}
                    className="w-full h-10 placeholder:p-0.5 p-3 shadow-sm border border-purple-800 shadow-slate-200 rounded-full relative my-2  md:w-full"
                  />
                  <div className="absolute mr-1 mb-0.5">
                    <button
                      className=" searchboxbtn "
                      onClick={handleApplyFilters}
                    >
                      <span className="pl-4 pr-4 px-[6px] py-[5px] bg-purple-900 text-base  rounded-full text-white font-sans font-medium">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          height="24px"
                          viewBox="0 -960 960 960"
                          width="24px"
                          fill="#e3e3e3"
                        >
                          <path d="M784-120 532-372q-30 24-69 38t-83 14q-109 0-184.5-75.5T120-580q0-109 75.5-184.5T380-840q109 0 184.5 75.5T640-580q0 44-14 83t-38 69l252 252-56 56ZM380-400q75 0 127.5-52.5T560-580q0-75-52.5-127.5T380-760q-75 0-127.5 52.5T200-580q0 75 52.5 127.5T380-400Z" />
                        </svg>
                      </span>
                    </button>
                  </div>
                </div>

                {/* Display selected areas as chips */}
                {filters.areas.map((area, index) => (
                  <div
                    key={index}
                    className="area-chip   inline-flex items-center justify-between bg-transparent text-purple-800 border border-purple-800"
                  >
                    <span className="truncate font-sans font-medium   ">
                      {area}
                    </span>
                    <button
                      className="remove-area-button text-white ml-2 flex-shrink-0"
                      onClick={() => handleRemoveArea(area)}
                    >
                      &times;
                    </button>
                  </div>
                ))}
                {showSuggestions && searchQuery && (
                  <div className="area-suggestions">
                    {filteredAreas.map((area, index) => (
                      <div
                        key={index}
                        className={`suggestion-item ${index === highlightedIndex ? "highlighted" : ""
                          }`}
                        onClick={() => handleSuggestionClick(area)}
                      >
                        {area}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <hr className="relative w-full  left-0 md:w-[100%] md:left-[10px]" />

              {isLoadingAreas && (
                <div className="w-full text-center text-[13px] font-sans font-medium text-gray-500 py-2">
                  Loading areas…
                </div>
              )}

              {!isLoadingAreas && areas.length === 0 && (
                <div className="w-full text-center text-[13px] font-sans font-medium text-gray-500 py-2">
                  No areas found for {filters.city}.
                </div>
              )}

              {displayedAreas.map((area, index) => (
                <label
                  key={index}
                  className={`checkbox-button  ${filters.areas.includes(area) ? "checkbox-selected" : ""
                    }`}
                >
                  <input
                    className="custome-checkbox"
                    type="checkbox"
                    name="areas"
                    value={area}
                    checked={filters.areas.includes(area)}
                    onChange={handleCheckboxChange}
                  />{" "}
                  {area}
                </label>
              ))}

              {areas.length > visibleAreasCount && (
                <div className="w-full flex items-center justify-center">
                  <div className="px-2 py-0.5  rounded-full hover:text-slate-400 border bg-purple-100  border-purple-200 hover:bg-purple-200 ">
                    {!isShowingAllAreas && (
                      <button
                        onClick={handleShowMoreAreas}
                        className="no-underline cursor-pointer  block w-full hover:text-slate-600"
                      >
                        <span class="material-symbols-outlined  pr-2">
                          expand_circle_down
                        </span>
                        <span className="hover:text-slate-950 text-[13px] font-sans font-medium text-gray-800   ">
                          Show More Areas
                        </span>
                        <span class="material-symbols-outlined pl-2">
                          expand_circle_down
                        </span>
                      </button>
                    )}

                    {isShowingAllAreas && (
                      <button
                        onClick={handleShowLessAreas}
                        className="rounded-full h-6   no-underline cursor-pointer block w-full"
                      >
                        <img
                          width="32"
                          height=""
                          className="pr-2 "
                          src="https://img.icons8.com/windows/32/circled-up-2.png"
                          alt="circled-up-2"
                        />
                        <span className="  text-gray-800 hover:text-slate-950">
                          Show Less
                        </span>
                        <img
                          width="32"
                          height=""
                          src="https://img.icons8.com/windows/32/circled-up-2.png"
                          alt="circled-up-2"
                        />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* BHK Section for Builder - Residential */}
          {activeTab === "builder" && filters.type === "Residential" && (
            <>
              <hr className="relative w-full left-0 md:w-[98%] md:left-[120px]" />
              <div className="mobile-hide">
                <h3 className="lg:hidden">BHK Type</h3>
              </div>
              <div className="filter-section2">
                <div className="filter-options-bhk lg:ml-8">
                  <div className="right-[135px] text-[#503691] relative hidden lg:block text-style">
                    BHK TYPE
                  </div>
                  <div className="filter-options-bhk">
                    {[
                      "Studio Apartment",
                      "1 BHK",
                      "2 BHK",
                      "3 BHK",
                      "4 BHK",
                      "5 BHK",
                      "6 BHK",
                      "6+ BHK",
                    ].map((bhk, index) => (
                      <label key={index}>
                        <input
                          className="checkbox"
                          type="checkbox"
                          name="bhks"
                          value={bhk}
                          checked={filters.bhks.includes(bhk)}
                          onChange={handleCheckboxChange}
                        />{" "}
                        {bhk}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <hr className="relative w-full left-0 md:w-[98%] md:left-[120px]" />
              <div className="mobile-hide">
                <h3 className="lg:hidden">PROPERTY AGE</h3>
              </div>
              <div className="filter-section2">
                <div className="filter-options-bhk lg:ml-8">
                  <div className="right-[135px] text-[#503691] relative hidden lg:block text-style">
                    PROPERTY AGE
                  </div>
                  <div className="filter-options-age">
                    {propertyAgeOptions.map((age, index) => (
                      <label key={index}>
                        <input
                          className="checkbox"
                          type="checkbox"
                          name="propertyAge"
                          value={age}
                          checked={filters.propertyAge.includes(age)}
                          onChange={handleCheckboxChange}
                        />{" "}
                        {age}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
          {(filters.type === "Residential Rent" ||
            filters.type === "Residential Sell") && (
              <>
                <hr className="relative w-full left-0 md:w-[98%] md:left-[120px]" />
                <div className="mobile-hide">
                  <h3 className="lg:hidden">BHK Type</h3>
                </div>
                <div className="filter-section2">
                  <div className="filter-options-bhk lg:ml-8">
                    <div className=" right-[135px] text-[#503691] relative hidden lg:block text-style">
                      BHK TYPE
                    </div>
                    <div className="filter-options-bhk">
                      {[
                        "1 BHK",
                        "2 BHK",
                        "3 BHK",
                        "4 BHK",
                        "5 BHK",
                        "6 BHK",
                        "6+ BHK",
                      ].map((bhks, index) => (
                        <label key={index}>
                          <input
                            className="checkbox"
                            type="checkbox"
                            name={activeTab === "requirement" ? "bhks" : "bhks"}
                            value={bhks}
                            checked={
                              activeTab === "requirement"
                                ? filters.bhks.includes(bhks)
                                : filters.bhks.includes(bhks)
                            }
                            onChange={handleCheckboxChange}
                          />{" "}
                          {bhks}
                        </label>
                      ))}
                    </div>{" "}
                    {/* closes .filter-options-bhk */}
                  </div>{" "}
                  {/* closes .filter-options-bhk lg:ml-8 */}
                </div>{" "}
                {/* closes .filter-section2 */}
              </>
            )}

          {/* PROPERTY AGE — shown for Residential and Commercial (Rent/Sell) */}
          {(filters.type === "Residential Rent" ||
            filters.type === "Residential Sell" ||
            filters.type === "Commercial Rent" ||
            filters.type === "Commercial Sell") && (
              <>
                <hr className="relative w-full left-0 md:w-[98%] md:left-[120px]" />
                <div className="mobile-hide">
                  <h3 className="lg:hidden">PROPERTY AGE</h3>
                </div>
                <div className="filter-section2">
                  <div className="filter-options-bhk lg:ml-8">
                    <div className="right-[135px] text-[#503691] relative hidden lg:block text-style">
                      PROPERTY AGE
                    </div>
                    <div className="filter-options-age">
                      {propertyAgeOptions.map((age, index) => (
                        <label key={index}>
                          <input
                            className="checkbox"
                            type="checkbox"
                            name="propertyAge"
                            value={age}
                            checked={filters.propertyAge.includes(age)}
                            onChange={handleCheckboxChange}
                          />{" "}
                          {age}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
<hr className="relative w-full left-0 md:w-[98%] md:left-[120px]" />

          {activeTab === "owner" && (
            <>

              <div className="mobile-hide">
                <h3 className="lg:hidden">Floor Type</h3>
              </div>

              <div className="filter-section2">
                <div className="filter-options-bhk lg:ml-8">

                  <div className="right-[135px] text-[#503691] relative hidden lg:block text-style">
                    FLOOR TYPE
                  </div>

                  <div className="filter-options-bhk">
                    {[
                      "Ground Floor",
                      "1st Floor",
                      "2nd–5th Floor",
                      "6th–10th Floor",
                      "Above 10th Floor",
                      "Not Specified",
                    ].map((floor, index) => {
                      // For Commercial Rent/Sell: disable all except Ground Floor and 1st Floor
                      const isCommercialDisabled = (filters.type === "Commercial Rent" || filters.type === "Commercial Sell") &&
                                                   floor !== "Ground Floor" && floor !== "1st Floor";
                      // For Residential Rent/Sell: disable all floor options
                      const isResidentialDisabled = filters.type === "Residential Rent" || filters.type === "Residential Sell";
                      const isDisabled = isCommercialDisabled || isResidentialDisabled;

                      return (
                        <label key={index} style={isDisabled ? {pointerEvents: "none", opacity: 0.5, filter: "blur(2px)"} : {}}>
                          <input
                            className="checkbox"
                            type="checkbox"
                            name="floors"
                            value={floor}
                            checked={filters.floors.includes(floor)}
                            onChange={handleCheckboxChange}
                            disabled={isDisabled}
                          />{" "}
                          {floor}
                        </label>
                      );
                    })}
                  </div>

                </div>
              </div>
            </>
          )}
          {/* Area Type Section for Builder - Residential */}
          {activeTab === "builder" && filters.type === "Residential" && (
            <>
              <hr className="relative w-full left-0 md:w-[98%] md:left-[120px]" />
              <div className="mobile-hide">
                <h3 className="lg:hidden">Type</h3>
              </div>
              <div className="filter-section2">
                <div className="filter-options-bhk lg:ml-8">
                  <div className="right-[135px] text-[#503691] relative hidden lg:block text-style">
                    AREA TYPE
                  </div>
                  <div className="filter-options-bhk">
                    {[
                      "Apartment",
                      "Bunglow",
                      "Weekend Home",
                      "Row House",
                      "Residential Plot",
                    ].map((areaType, index) => (
                      <label key={index}>
                        <input
                          className="checkbox"
                          type="checkbox"
                          name="subType"
                          value={areaType}
                          checked={filters.subType.includes(areaType)}
                          onChange={handleCheckboxChange}
                        />{" "}
                        {areaType}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
          {/* Area Type Section for Builder - Commercial */}
          {activeTab === "builder" && filters.type === "Commercial" && (
            <>
              <hr className="relative w-full left-0 md:w-[98%] md:left-[120px]" />
              <div className="mobile-hide">
                <h3 className="lg:hidden">Type</h3>
              </div>
              <div className="filter-section2">
                <div className="filter-options-bhk lg:ml-8">
                  <div className="right-[135px] text-[#503691] relative hidden lg:block text-style">
                    AREA TYPE
                  </div>
                  <div className="filter-options-bhk">
                    {[
                      "Shop",
                      "Showroom",
                      "Industrial Land",
                      "Office",
                      "Industrial Shed",
                    ].map((areaType, index) => (
                      <label key={index}>
                        <input
                          className="checkbox"
                          type="checkbox"
                          name="subType"
                          value={areaType}
                          checked={filters.subType.includes(areaType)}
                          onChange={handleCheckboxChange}
                        />{" "}
                        {areaType}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          {(filters.type === "Residential Rent" ||
            filters.type === "Residential Sell") && (
              <>
                <hr className="relative w-full left-0 md:w-[98%] md:left-[120px]" />
                <div className="mobile-hide">
                  <h3 className="lg:hidden">TYPE</h3>
                </div>
                <div className="filter-section2 lg:ml-[71px] ">
                  <div className="filter-options">
                    <div className="pt-4 right-[170px] text-[#503691] relative hidden lg:block text-style">
                      TYPE
                    </div>
                    <div className="filter-options type-PR">
                      {[
                        "Apartment",
                        "Bungalow",
                        "Tenement",
                        "Penthouse",
                        "Weekend Home",
                        "Rowhouse",
                        "Residential Plot",
                        "PG",
                        ...(filters.type === "Residential Sell"
                          ? ["Pre Leased Spaces"]
                          : []), // Add 'Pre Leased Spaces' only for Residential Sell
                      ].map((subType, index) => (
                        <label key={index}>
                          <input
                            className="checkbox"
                            type="checkbox"
                            name="subType"
                            value={subType}
                            checked={filters.subType.includes(subType)}
                            onChange={handleCheckboxChange}
                          />{" "}
                          {subType}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}

          {(filters.type === "Commercial Rent" ||
            filters.type === "Commercial Sell") && (
              <>
                <hr className="relative  w-full left-0 md:w-[98%] md:left-[120px]" />

                <div className="mobile-hide">
                  <h3>TYPE</h3>
                </div>

                <div className="filter-section2 lg:ml-[120px] ">
                  <div className="top-10 left-[-790px] text-[#503691] relative hidden lg:block text-style">
                    TYPE
                  </div>
                  <div className="typeCheckbox ">
                    {[
                      "Basement",
                      "Commercial Plot",
                      "Shed",
                      "Co Working Space",
                      "Factory",
                      "Shop",
                      "Commercial Building",
                      "Godown",
                      "Showroom",
                      "Restaurant",
                      "Industrial Land",
                      "Commercial Flat",
                      "Office",
                      "Ware House",
                      "Pre Leased Spaces",
                      "Commericial Bunglow",
                      "Land",
                      "Agriculture Land",
                      "PG House",
                      "Hotel",
                      "Gym",
                      "Spa",
                      "Hospital",
                      "Salon",
                      "Petrol Pump",
                      "Cafe",
                      "PreSchool",
                      ...(filters.type === "Commercial Sell"
                        ? ["Pre Leased Spaces"]
                        : []), // Add 'Pre Leased Spaces' only for Commercial Sell
                    ].map((subType, index) => (
                      <div>
                        <label key={index}>
                          <input
                            className="typeCheckboxList"
                            type="checkbox"
                            name="subType"
                            value={subType}
                            checked={filters.subType.includes(subType)}
                            onChange={handleCheckboxChange}
                          />{" "}
                          {subType}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          <hr className="relative w-full left-0 md:w-[98%] md:left-[120px]" />

          {activeTab !== "requirement" && (
            <>
              <div className="filter-section lg:ml-28 mt-4">
                <label className="flex items-center space-x-2 text-purple-900 font-medium">
                  <input
                    className="checkbox"
                    type="checkbox"
                    name="verifiedOnly"
                    value={filters.verifiedOnly}
                    checked={filters.verifiedOnly}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        verifiedOnly: e.target.checked,
                      }))
                    }
                  />
                  <span>Show only verified properties</span>
                </label>
              </div>
            </>
          )}
          <hr className="relative w-full left-0 md:w-[98%] md:left-[120px]" />

          {/* Family Filter - Only visible for Residential Rent */}
          {filters.type === "Residential Rent" &&
            activeTab !== "requirement" && (
              <>
                <div className="mobile-hide">
                  <h3 className="lg:hidden">FAMILY FILTER</h3>
                </div>

                <div className="filter-section1 lg:ml-[-20px]">
                  <div className="filter-options2">
                    <div className="pt-4 right-[90px] text-[#503691] relative hidden lg:block text-style">
                      FAMILY FILTER
                    </div>

                    <div className="filter-options2">
                      {["Family-Bachelors", "Only For Family"].map((type) => {
                        const selected =
                          filters.livingTypeFilter?.split("|") || [];

                        return (
                          <label key={type}>
                            <input
                              className="checkbox"
                              type="checkbox"
                              name="livingTypeFilter"
                              value={type}
                              checked={selected.includes(type)}
                              onChange={(e) => {
                                setFilters((prev) => {
                                  const current = prev.livingTypeFilter
                                    ? prev.livingTypeFilter.split("|")
                                    : [];

                                  let updated;

                                  if (e.target.checked) {
                                    updated = current.includes(type)
                                      ? current
                                      : [...current, type];
                                  } else {
                                    updated = current.filter((i) => i !== type);
                                  }

                                  return {
                                    ...prev,
                                    livingTypeFilter:
                                      updated.length > 0
                                        ? updated.join("|")
                                        : "",
                                  };
                                });
                              }}
                            />{" "}
                            {type}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <hr className="relative w-full left-0 md:w-[98%] md:left-[120px]" />
              </>
            )}
          <div className="mobile-hide">
            <h3 className="lg:hidden">BUDGET</h3>
          </div>
          <div className="filter-section lg:ml-6">
            <div className="input-row">
              <div className="pt-4 right-[130px] text-[#503691] relative hidden lg:block text-style">
                BUDGET
              </div>
              <div className="flex flex-col w-full max-w-[300px] m-[5px]">
                <div className="budget-input-container !m-0">
                  <input
                    type="text"
                    name="minRent"
                    placeholder="Enter Min rent..."
                    className="budget-input"
                    value={filters.minRent}
                    onChange={handleInputChange}
                  />
                  {formatInputPrice(filters.minRent) !== filters.minRent && (
                    <div className="text-lg text-purple-900 font-sans font-medium pr-[10px] whitespace-nowrap">
                      {formatInputPrice(filters.minRent)}
                    </div>
                  )}
                  <div className="budget-max">Min</div>
                </div>

              </div>
              <div className="flex flex-col w-full max-w-[300px] m-[5px]">
                <div className="budget-input-container !m-0">
                  <input
                    type="text"
                    name="maxRent"
                    placeholder="Enter Max rent..."
                    className="budget-input"
                    value={filters.maxRent}
                    onChange={handleInputChange}
                  />
                  {formatInputPrice(filters.maxRent) !== filters.maxRent && (
                    <div className="text-lg text-purple-900 font-sans font-medium pr-[10px] whitespace-nowrap">
                      {formatInputPrice(filters.maxRent)}
                    </div>
                  )}
                  <div className="budget-max">Max</div>
                </div>
              </div>
            </div>
          </div>
          <hr className="relative w-full left-0 md:w-[98%] md:left-[120px]" />
          <div className="mobile-hide">
            <h3 className="lg:hidden">Size</h3>
          </div>
          <div className="filter-section lg:ml-12">
            <div className="input-row">
              <div className="pt-4 right-[150px] text-[#503691] relative hidden lg:block text-style">
                SIZE
              </div>

              <div className="budget-input-container">
                <input
                  type="text"
                  name={filters.sizeUnit === "sqYd" ? "minsqYd" : "minsqFt"}
                  placeholder={`Min ${filters.sizeUnit}`}
                  className="budget-input"
                  value={
                    filters.sizeUnit === "sqYd"
                      ? filters.minsqYd
                      : filters.minsqFt
                  }
                  onChange={handleInputChange}
                />
                <div className="budget-max">Min</div>
              </div>
              <div className="budget-input-container">
                <input
                  type="text"
                  name={filters.sizeUnit === "sqYd" ? "maxsqYd" : "maxsqFt"}
                  placeholder={`Max ${filters.sizeUnit}`}
                  className="budget-input"
                  value={
                    filters.sizeUnit === "sqYd"
                      ? filters.maxsqYd
                      : filters.maxsqFt
                  }
                  onChange={handleInputChange}
                />
                <div className="budget-max">Max</div>
              </div>
              {/* 
              <div className="flex items-center ml-2">
                <button
                  className={`px-3 py-1 rounded-full border ${
                    filters.sizeUnit === "sqFt"
                      ? "bg-purple-900 text-white"
                      : "bg-white text-purple-900 border-purple-900"
                  }`}
                  onClick={() => {
                    setFilters((prev) => ({ ...prev, sizeUnit: "sqFt" }));
                  }}
                >
                  SqFt
                </button>
              </div> */}
            </div>

            {/* Additional broker/builder-only filters: Age and Bathrooms */}
            {activeTab === "broker" && (
              <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Age Filter */}
                <div>
                  <div className="text-[#503691] relative hidden lg:block text-style mb-2">
                    AGE
                  </div>
                  <select
                    name="age"
                    value={filters.age}
                    onChange={handleInputChange}
                    className="w-full h-10 p-2 border border-purple-800 rounded-lg shadow-sm"
                  >
                    <option value="">Select Age</option>
                    <option value="1-5 Years">1-5 Years</option>
                    <option value="5-10 Years">5-10 Years</option>
                    <option value="10-15 Years">10-15 Years</option>
                    <option value="15-20 Years">15-20 Years</option>
                    <option value="20-25 Years">20-25 Years</option>
                    <option value="25-30 Years">25-30 Years</option>
                    <option value="30-35 Years">30-35 Years</option>
                    <option value="35-40 Years">35-40 Years</option>
                    <option value="40-45 Years">40-45 Years</option>
                    <option value="45-50 Years">45-50 Years</option>
                    <option value="50 & Above">50 & Above</option>
                    <option value="Newly Constructed">Newly Constructed</option>
                  </select>
                </div>
                {/* Bathrooms Filter */}
                <div>
                  <div className="text-[#503691] relative hidden lg:block text-style mb-2">
                    BATHROOMS
                  </div>
                  <select
                    name="bathrooms"
                    value={filters.bathrooms}
                    onChange={handleInputChange}
                    className="w-full h-10 p-2 border border-purple-800 rounded-lg shadow-sm"
                  >
                    <option value="">Select Bathrooms</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4+">4+</option>
                  </select>
                </div>
              </div>
            )}
            {activeTab === "builder" && (
              <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Age Filter */}
                <div>
                  <div className="text-[#503691] relative hidden lg:block text-style mb-2">
                    PROPERTY STATUS
                  </div>
                  <select
                    name="age"
                    value={filters.age}
                    onChange={handleInputChange}
                    className="w-full h-10 p-2 border border-purple-800 rounded-lg shadow-sm"
                  >
                    <option value="">Select Option</option>
                    <option value="Upcoming Project">Upcoming Project</option>
                    <option value="Under Construction">
                      Under Construction
                    </option>
                    <option value="Ready to Move">Ready to Move</option>
                  </select>
                </div>
                {/* Bathrooms Filter */}
                <div>
                  <div className="text-[#503691] relative hidden lg:block text-style mb-2">
                    BATHROOMS
                  </div>
                  <select
                    name="bathrooms"
                    value={filters.bathrooms}
                    onChange={handleInputChange}
                    className="w-full h-10 p-2 border border-purple-800 rounded-lg shadow-sm"
                  >
                    <option value="">Select Bathrooms</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4+">4+</option>
                  </select>
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3 mb-5 ml-20">
            <button
              className="apply-filters-button"
              onClick={handleApplyFilters}
            >
              Search
            </button>
            <input
              type="text"
              value={saveSearchName}
              onChange={(e) => setSaveSearchName(e.target.value)}
              placeholder="Name this search (optional)"
              className="h-10 px-3 rounded-lg border border-purple-800 shadow-sm text-sm"
            />
            <button
              className="apply-filters-button"
              style={{ opacity: isSavingSearch ? 0.6 : 1 }}
              disabled={isSavingSearch}
              onClick={handleSaveSearch}
            >
              {isSavingSearch ? "Saving..." : "Save Search"}
            </button>
          </div>
        </div>
      </div>
      </div>
      <ProBrokerFooter />
    </>
  );  
};

export default FilterPage;
